import React, { useEffect, useState, useCallback } from "react";
import { PlusCircle, MinusCircle, Target, Award, UserPlus, Star, SlidersHorizontal } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import { useLang } from "../../i18n";
import { useConfig } from "../../context/ConfigContext";
import MagicalCard from "../../components/MagicalCard";
import Avatar from "../../components/Avatar";
import { StatCard, EmptyState, SkeletonPanel } from "../../components/common";
import AwardPointsModal from "../../components/AwardPointsModal";
import DeductPointsModal from "../../components/DeductPointsModal";
import AwardBadgeModal from "../../components/AwardBadgeModal";
import ChallengeFormModal from "../../components/ChallengeFormModal";
import AddStudentModal from "../../components/AddStudentModal";
import WorkspaceLabelsModal from "../../components/WorkspaceLabelsModal";
import { fmtRelative } from "../../utils";

export default function TeacherDashboard() {
  const { t, pick, lang } = useLang();
  const { config } = useConfig();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [students, setStudents] = useState([]);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/staff/dashboard");
    setData(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openModal = async (m) => {
    if (students.length === 0) {
      const { data } = await api.get("/staff/students");
      setStudents(data.students);
    }
    setModal(m);
  };

  if (!data) return <div className="grid gap-4 md:grid-cols-4"><SkeletonPanel /><SkeletonPanel /><SkeletonPanel /><SkeletonPanel /></div>;

  const actions = [
    { key: "award", icon: PlusCircle, label: t("teacher.add_points"), cls: "btn-magic" },
    { key: "deduct", icon: MinusCircle, label: t("teacher.deduct_points"), cls: "btn-magic btn-pink" },
    { key: "challenge", icon: Target, label: t("teacher.create_challenge"), cls: "btn-magic btn-cyan" },
    { key: "badge", icon: Award, label: t("teacher.award_badge"), cls: "btn-magic btn-gold" },
    { key: "student", icon: UserPlus, label: t("teacher.add_student"), cls: "btn-magic btn-ghost" },
    { key: "ws", icon: SlidersHorizontal, label: t("teacher.workspace_settings"), cls: "btn-magic btn-ghost" },
  ];

  return (
    <div className="space-y-8" data-testid="teacher-dashboard">
      <h2 className="text-2xl sm:text-3xl font-black text-center">{t("teacher.dashboard")}</h2>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-students" icon="Users" label={t("teacher.students_count")} value={data.student_count} />
        <StatCard testId="stat-week-points" icon="Star" label={t("teacher.points_this_week")} value={`+${data.week_points}`} gradient="from-[#FFAE32] to-[#FFC83D]" />
        <StatCard testId="stat-challenges" icon="Target" label={t("teacher.active_challenges")} value={data.active_challenges} gradient="from-[#268CEB] to-[#27C9EE]" />
        <StatCard testId="stat-approvals" icon="Clock" label={t("teacher.approvals")} value={data.awaiting_approvals} gradient="from-[#EF4D9A] to-[#FF63B5]" />
      </div>

      <MagicalCard title={t("teacher.quick_actions")} testId="quick-actions" hover={false}>
        <div className="flex flex-wrap justify-center gap-3">
          {actions.map((a) => (
            <button key={a.key} data-testid={`quick-${a.key}`} onClick={() => openModal(a.key)}
              className={`${a.cls} text-sm`}>
              <a.icon className="w-4 h-4" /> {a.label}
            </button>
          ))}
        </div>
      </MagicalCard>

      <div className="grid md:grid-cols-2 gap-6">
        <MagicalCard title={t("teacher.top_student")} variant="ribbon-gold" testId="top-student-card">
          {data.top_student ? (
            <button onClick={() => navigate(`/profile/${data.top_student.student_id}`)} className="w-full flex items-center gap-4 group">
              <Avatar src={data.top_student.avatar} name={pick(data.top_student, "name")} size="lg" crown />
              <div className="text-start">
                <p className="text-xl font-black group-hover:text-[#FFC83D] transition-colors">{pick(data.top_student, "name")}</p>
                <p className="flex items-center gap-1 text-[#FFC83D] font-black">
                  <Star className="w-4 h-4" fill="currentColor" /> {data.top_student.points} {t("common.points")}
                </p>
              </div>
            </button>
          ) : <EmptyState icon="Trophy" title={t("lb.empty")} testId="top-student-empty" />}
        </MagicalCard>

        <MagicalCard title={t("teacher.near_level_up")} variant="ribbon-cyan" testId="near-level-card">
          {data.near_level_up.length === 0 ? (
            <EmptyState icon="Sparkles" title={t("common.empty")} testId="near-level-empty" />
          ) : (
            <ul className="space-y-2">
              {data.near_level_up.map((s) => (
                <li key={s.id} data-testid={`near-level-${s.id}`}>
                  <button onClick={() => navigate(`/profile/${s.id}`)} className="w-full flex items-center gap-3 rounded-xl bg-[#0D0B38]/50 border border-white/8 px-3 py-2 hover:border-[#FFC83D]/40 transition-colors">
                    <Avatar src={s.avatar} name={pick(s, "name")} size="xs" />
                    <span className="flex-1 text-start text-sm font-bold">{pick(s, "name")}</span>
                    <span className="text-xs font-extrabold text-[#FFC83D]">
                      {t("teacher.remaining", { n: s.remaining, level: pick(s, "next_level") })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </MagicalCard>
      </div>

      <MagicalCard title={t("teacher.recent_activity")} variant="ribbon-pink" testId="recent-activity-card">
        {data.recent_activity.length === 0 ? (
          <EmptyState icon="History" title={t("common.empty")} testId="activity-empty" />
        ) : (
          <ul className="space-y-2">
            {data.recent_activity.map((a) => (
              <li key={a.id} data-testid={`activity-${a.id}`} className="flex items-center gap-3 rounded-xl bg-[#0D0B38]/50 border border-white/8 px-3 py-2.5">
                <Avatar src={a.student_avatar} name={lang === "ar" ? a.student_name_ar : a.student_name_en} size="xs" />
                <span className="flex-1 text-sm font-bold truncate">
                  {lang === "ar" ? a.student_name_ar : a.student_name_en}
                  <span className={`mx-2 font-black ${a.amount > 0 ? "text-[#48D276]" : "text-[#FF8FB1]"}`} dir="ltr">
                    {a.amount > 0 ? `+${a.amount}` : a.amount}
                  </span>
                  <span className="text-[#A5A0D6] font-semibold text-xs">{a.reason}</span>
                </span>
                <span className="text-[10px] text-[#A5A0D6] font-bold shrink-0">{fmtRelative(a.created_at, lang)}</span>
              </li>
            ))}
          </ul>
        )}
      </MagicalCard>

      <AwardPointsModal open={modal === "award"} onClose={() => setModal(null)} students={students}
        categories={config?.categories || []} onDone={load} />
      <DeductPointsModal open={modal === "deduct"} onClose={() => setModal(null)} students={students} onDone={load} />
      <AwardBadgeModal open={modal === "badge"} onClose={() => setModal(null)} students={students} onDone={load} />
      <ChallengeFormModal open={modal === "challenge"} onClose={() => setModal(null)} onDone={load} />
      <AddStudentModal open={modal === "student"} onClose={() => setModal(null)} onDone={load} />
      <WorkspaceLabelsModal open={modal === "ws"} onClose={() => setModal(null)} />
    </div>
  );
}
